// PRODUCTION — flow editor dropdown (same chrome as Formik `ProtoSelect` in StepConfigForms)
import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import type { BasicSelectOption } from 'src/components/shared/form/BasicSelect'
import {
  protoSelectChevronCss,
  protoSelectEmptyCss,
  protoSelectLabelCss,
  protoSelectMenuCss,
  protoSelectOptionCss,
  protoSelectOptionListCss,
  protoSelectRootCss,
  protoSelectSearchInputCss,
  protoSelectSearchRowCss,
  protoSelectTriggerCss,
} from './flowEditorProtoSelectStyles'
import { PROTO_SELECT_SEARCH_THRESHOLD, filterProtoSelectOptions } from './protoSelectFilter'

export interface ProtoSelectStandaloneProps {
  disabled?: boolean
  onOptionSelect?: (option: BasicSelectOption) => void
  onValueChange: (value: string | number) => void
  options: BasicSelectOption[]
  placeholder?: string
  /** Defaults to on once the list is long enough to be hard to scan. */
  searchable?: boolean
  searchPlaceholder?: string
  value: string | number | ''
}

export function ProtoSelectStandalone({
  disabled = false,
  onOptionSelect,
  onValueChange,
  options,
  placeholder = 'Select an option',
  searchable,
  searchPlaceholder = 'Search…',
  value,
}: ProtoSelectStandaloneProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const ref = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const listboxId = `${useId()}-listbox`
  const optionId = (index: number) => `${listboxId}-option-${index}`
  const selected = options.find((option) => String(option.value) === String(value))
  const isSearchable = searchable ?? options.length >= PROTO_SELECT_SEARCH_THRESHOLD

  const visibleOptions = useMemo(() => {
    if (!isSearchable) {
      return options
    }

    return filterProtoSelectOptions(options, query)
  }, [isSearchable, options, query])

  useEffect(() => {
    if (!open) {
      return
    }

    const handler = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handler)

    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Read through a ref so a caller passing a fresh `options` array on every render cannot
  // wipe a half-typed query — the reset below must fire only on the open transition.
  const selectedIndexRef = useRef(-1)

  selectedIndexRef.current = options.findIndex((option) => String(option.value) === String(value))

  // Opening starts clean: empty query, highlight on whatever is currently selected.
  useEffect(() => {
    if (!open) {
      return
    }

    setQuery('')
    setHighlightedIndex(selectedIndexRef.current)

    if (isSearchable) {
      searchInputRef.current?.focus()
    }
  }, [open, isSearchable])

  // A narrowed list invalidates the old index, so fall back to the first match.
  useEffect(() => {
    if (query !== '') {
      setHighlightedIndex(visibleOptions.length ? 0 : -1)
    }
  }, [query, visibleOptions.length])

  useEffect(() => {
    if (!open || highlightedIndex < 0) {
      return
    }

    const highlightedNode = listRef.current?.children[highlightedIndex]

    if (highlightedNode) {
      highlightedNode.scrollIntoView({ block: 'nearest' })
    }
  }, [open, highlightedIndex])

  const selectOption = (option: BasicSelectOption) => {
    onValueChange(option.value)
    onOptionSelect?.(option)
    setOpen(false)
    triggerRef.current?.focus()
  }

  const moveHighlight = (offset: number) => {
    if (!visibleOptions.length) {
      return
    }

    setHighlightedIndex((current) => {
      const next = current + offset

      if (next < 0) {
        return visibleOptions.length - 1
      }

      if (next >= visibleOptions.length) {
        return 0
      }

      return next
    })
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) {
      return
    }

    if (event.key === 'Escape') {
      event.preventDefault()
      setOpen(false)
      triggerRef.current?.focus()

      return
    }

    if (!open) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault()
        setOpen(true)
      }

      return
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      moveHighlight(1)

      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      moveHighlight(-1)

      return
    }

    if (event.key === 'Enter') {
      // Always swallow Enter while the menu is open. These controls sit inside a Formik
      // `<Form>`, and letting it through submits the step config — whether it does depends
      // on how many sibling inputs block implicit submission, which is not worth relying on.
      event.preventDefault()

      const highlighted = visibleOptions[highlightedIndex]

      if (highlighted) {
        selectOption(highlighted)
      }
    }
  }

  return (
    <div ref={ref} css={protoSelectRootCss} onKeyDown={handleKeyDown}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        // `combobox` (rather than a plain button) is what makes this element a legal owner of
        // `aria-activedescendant`, so the highlighted option can be announced without moving focus.
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open && !disabled}
        aria-controls={open && !disabled ? listboxId : undefined}
        aria-activedescendant={
          open && !disabled && !isSearchable && highlightedIndex >= 0
            ? optionId(highlightedIndex)
            : undefined
        }
        onClick={() => {
          if (!disabled) {
            setOpen((previous) => !previous)
          }
        }}
        css={protoSelectTriggerCss(disabled, open && !disabled, Boolean(selected))}
      >
        <span css={protoSelectLabelCss}>{selected ? selected.label : placeholder}</span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          stroke={disabled ? 'var(--mui-shades-300)' : 'var(--mui-shades-400)'}
          css={protoSelectChevronCss(open)}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && !disabled && (
        <div css={protoSelectMenuCss}>
          {isSearchable && (
            <div css={protoSelectSearchRowCss}>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--mui-shades-400)"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded
                value={query}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                aria-controls={listboxId}
                aria-activedescendant={
                  highlightedIndex >= 0 ? optionId(highlightedIndex) : undefined
                }
                onChange={(event) => setQuery(event.target.value)}
                css={protoSelectSearchInputCss}
              />
            </div>
          )}

          <div ref={listRef} id={listboxId} role="listbox" css={protoSelectOptionListCss}>
            {visibleOptions.map((option, index) => {
              const isActive = String(option.value) === String(value)

              return (
                <div
                  key={String(option.value)}
                  id={optionId(index)}
                  role="option"
                  aria-selected={isActive}
                  tabIndex={-1}
                  onClick={() => selectOption(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  css={protoSelectOptionCss(isActive, index === highlightedIndex)}
                >
                  <span>{option.label}</span>
                  {isActive && (
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="var(--mui-primary-500)"
                      strokeWidth={2.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
              )
            })}
          </div>

          {visibleOptions.length === 0 && (
            <div role="status" css={protoSelectEmptyCss}>
              No matches
            </div>
          )}
        </div>
      )}
    </div>
  )
}
